import React from 'react';

import { Adult, Bed, NoSmoking, OceanView, RoomSize, Smoking } from '@travel/icons/service';
import { MOBILE_SCREEN, useDeviceType, useGetTranslation } from '@travel/traveler-core/hooks';
import { IconText, Tag } from '@travel/ui';
import { cx } from '@travel/utils';

import styles from './roomBasicInfoSummary.module.scss';

type Props = {
  customStyle?: 'text' | 'iconText';
  itemClassName?: string;
  tagItemsWrapperClassName?: string;
  /** String of room item id */
  bedText?: string;
  roomText?: string | null;
  capacityText?: string | [];
  smokingAllowed?: boolean | null;
  viewText?: string | null;
};

function RoomBasicInfoSummary(props: Props) {
  const iconTextSpacing = 6;
  const { bedText, roomText, capacityText, smokingAllowed, viewText } = props;

  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;

  const getTranslation = useGetTranslation();

  const smokingAllowedText = smokingAllowed
    ? getTranslation({
        id: 'Provider_Info.Luxury.Yes_Smoking',
      })
    : smokingAllowed === false
      ? getTranslation({
          id: 'Provider_Info.Luxury.No_Smoking',
        })
      : null;

  if (props.customStyle === 'text') {
    return (
      <>
        {bedText}
        {bedText && capacityText ? ', ' : ''}
        {capacityText}
        {(bedText || capacityText) && smokingAllowedText ? ', ' : ''}
        {smokingAllowedText}
      </>
    );
  }

  const itemClassName = cx(styles.iconText, props.itemClassName);

  return (
    <>
      <div
        className={cx(styles.itemsWithTag, props.tagItemsWrapperClassName)}
        data-testid="roomBasicInfoSummary-wrapper"
      >
        {smokingAllowedText && (
          <Tag type="outlined" color="jet" className={styles.tagItem}>
            {isMobile ? (
              smokingAllowedText
            ) : (
              <IconText
                className={itemClassName}
                textClassName={styles.iconLabelText}
                spaceBetween={iconTextSpacing}
                text={smokingAllowedText}
                icon={smokingAllowed ? <Smoking size={16} /> : <NoSmoking size={16} />}
              />
            )}
          </Tag>
        )}
        {bedText && (
          <Tag type="outlined" color="jet" className={styles.tagItem}>
            {isMobile ? (
              bedText
            ) : (
              <IconText
                className={itemClassName}
                textClassName={styles.iconLabelText}
                spaceBetween={iconTextSpacing}
                text={bedText}
                icon={<Bed size={16} />}
              />
            )}
          </Tag>
        )}
        {viewText && (
          <Tag type="outlined" color="jet" className={styles.tagItem}>
            {isMobile ? (
              viewText
            ) : (
              <IconText
                className={itemClassName}
                textClassName={styles.iconLabelText}
                spaceBetween={iconTextSpacing}
                text={viewText}
                icon={<OceanView size={16} />}
              />
            )}
          </Tag>
        )}
      </div>
      <div className={styles.itemsWithoutTag}>
        {roomText && (
          <IconText
            className={itemClassName}
            textClassName={styles.iconLabelText}
            spaceBetween={iconTextSpacing}
            text={roomText}
            icon={<RoomSize size={16} />}
          />
        )}
        {capacityText && (
          <IconText
            className={itemClassName}
            textClassName={styles.iconLabelText}
            spaceBetween={iconTextSpacing}
            text={capacityText}
            icon={<Adult size={16} />}
          />
        )}
      </div>
    </>
  );
}

export default RoomBasicInfoSummary;
